import { IGameStats } from '@shared/types';
import React, { JSX } from 'react';
import { CategoryName, IMatchup } from 'types';

export const getNumberEnding = (num: number): string => {
  return getEnding(num);
};

const getEnding = (num: number): string => {
  if (num > 10 && num < 20) {
    return 'th';
  }
  switch (num % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const formatPercentage = (num: number): string => {
  return `${num.toFixed(1)}%`;
};

export const formatPoints = (num: number, divisor: number = 1): string => {
  const quotient = num / divisor;
  const result = divisor === 1 ? Math.round(quotient) : quotient.toFixed(1);
  const isOver3digits = String(result).length > 3;
  return isOver3digits ? `${result}` : `${result} pts`;
};

export const formatYards = (num: number, divisor: number = 1): string => {
  const quotient = num / divisor;
  const result = divisor === 1 ? Math.round(quotient) : quotient.toFixed(1);
  const isOver3digits = String(result).length > 5;
  return isOver3digits ? `${result}` : `${result} yds`;
};

export const formatInterceptions = (num: number, divisor: number = 1): string => {
  const quotient = num / divisor;
  const result = divisor === 1 ? Math.round(quotient) : quotient.toFixed(1);
  const isOver3digits = String(result).length > 5;
  return isOver3digits ? `${result}` : `${result} INTs`;
};

export const formatTDs = (num: number, divisor: number = 1): string => {
  const quotient = num / divisor;
  const result = divisor === 1 ? Math.round(quotient) : quotient.toFixed(1);
  const isOver3digits = String(result).length > 5;
  return isOver3digits ? `${result}` : `${result} TDs`;
};

export const formatFumbles = (num: number, divisor: number = 1): string => {
  const quotient = num / divisor;
  const result = divisor === 1 ? Math.round(quotient) : quotient.toFixed(1);
  const isOver3digits = String(result).length > 5;
  return isOver3digits ? `${result}` : `${result} FUMs`;
};

export const formatPenalties = (num: number, divisor: number = 1): string => {
  const quotient = num / divisor;
  const result = divisor === 1 ? quotient : quotient.toFixed(1);
  const isOver3digits = String(result).length > 3;
  return isOver3digits ? `${result}` : `${result} penalties`;
};

export const formatPlays = (num: number, rankCategory?: string): string => {
  const isOver3digits = String(num).length > 3;
  if (isOver3digits) {
    return `${num}`;
  }
  switch (rankCategory) {
    case CategoryName.Penalties:
      return `${num} pens`;
    case CategoryName.TotalYards:
    case CategoryName.TotalSackedYardsLost:
    case CategoryName.PenaltyYards:
      return `${num} yds`;
    case CategoryName.InterceptionsThrown:
    case CategoryName.DefensiveInterceptions:
      return `${num} INTs`;
    case CategoryName.FumblesLost:
    case CategoryName.FumblesRecovered:
      return `${num} FUMs`;
    case CategoryName.ScoredRushingTDs:
      return `${num} TDs`;
    case CategoryName.FourthDownEfficiency:
      return '%';
    case CategoryName.TotalPlays:
    default:
      return `${num} ${num === 1 ? 'play' : 'plays'}`;
  }
};

export const getGameStatus = (gameStats?: IGameStats | IMatchup): string => {
  if (!gameStats) return '◦';

  if (gameStats.gameStatus.startsWith('Final') || gameStats.gameStatus.startsWith('Completed')) {
    return 'F';
  }

  if (gameStats.gameStatus.startsWith('Scheduled')) {
    return 'S';
  }

  return 'IP';
};

/**
 * Simple text parser that converts a {string} into a {JSX.Element} and wraps words
 * surrounded by backticks into `<span className={cls}>words...</span>`
 *
 * @param {string} text - string to be parsed
 * @param {string} cls - class to assign to the `<span>` if text contains words wrapped in backticks
 *
 * @return {JSX.Element} `JSX.Element` component with words wrapped in `<span>`s that were wrapped with backticks in a passed in `text`
 */
export const parseText = (text: string, cls: string): JSX.Element => {
  if (!text || text.length === 0) return <></>;

  // Split the text by backtick
  const parts = text.split('`');
  return (
    <React.Fragment>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className={cls}>
            {part}
          </span>
        ) : (
          part
        )
      )}
    </React.Fragment>
  );
};

/**
 * Convert camelCase string to Title Case words
 *
 * Transforms camelCase or pascalCase strings into human-readable Title Case format
 * by inserting spaces before capital letters and capitalizing the first letter.
 *
 * @param {string} str - The camelCase string to convert (e.g., 'totalFirstDowns')
 *
 * @return {string} The Title Case version (e.g., 'Total First Downs')
 *
 * @example
 * camelCaseToTitleCase('totalFirstDowns') // => 'Total First Downs'
 * camelCaseToTitleCase('passingYards') // => 'Passing Yards'
 * camelCaseToTitleCase('pointsAllowed') // => 'Points Allowed'
 */
export const camelCaseToTitleCase = (str: string): string => {
  return str
    .replace(/([A-Z])/g, ' $1') // Insert space before capitals
    .replace(/^./, (char) => char.toUpperCase()) // Capitalize first letter
    .trim();
};
